import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import axios from 'axios';

const ProductHistoryModal = ({ open, onClose, product, marketCode = 'US' }) => {
  const [loading, setLoading] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (open && product) {
      fetchHistory();
    }
  }, [open, product, marketCode]);

  const fetchHistory = async () => {
    if (!product || !product.asin) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`/api/products/${product.asin}/history`, {
        params: {
          platform: marketCode,
          days: 30
        }
      });

      if (response.data.success) {
        setHistoryData(response.data);
      } else {
        setError(response.data.message || 'Query failed');
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
      setError(err.response?.data?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (!num) return 'N/A';
    return num.toLocaleString();
  };

  const formatCurrency = (num) => {
    if (!num) return 'N/A';
    return `$${parseFloat(num).toFixed(2)}`;
  };

  const getTrendIcon = (change) => {
    if (change > 0) return <TrendingUpIcon color="success" />;
    if (change < 0) return <TrendingDownIcon color="error" />;
    return <TrendingFlatIcon color="disabled" />;
  };

  const getRankTrendIcon = (change) => {
    // Rank: lower number is better
    if (change > 0) return <TrendingUpIcon color="success" />; // Rank improved
    if (change < 0) return <TrendingDownIcon color="error" />; // Rank declined
    return <TrendingFlatIcon color="disabled" />;
  };

  const renderStats = () => {
    if (!historyData?.stats) return null;

    const { stats } = historyData;

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Rank Change */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Rank Change</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {getRankTrendIcon(stats.changes.rank.value)}
                <Typography variant="h6" sx={{ ml: 1 }}>
                  #{stats.latest.rank}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {stats.changes.rank.value === 0 ? (
                  'No change'
                ) : (
                  <>
                    {stats.changes.rank.improved ? '↑' : '↓'} {Math.abs(stats.changes.rank.value)} positions
                    <span style={{ marginLeft: 4 }}>
                      (#{stats.oldest.rank} → #{stats.latest.rank})
                    </span>
                  </>
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Price Change */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Price Change</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {getTrendIcon(stats.changes.price.value)}
                <Typography variant="h6" sx={{ ml: 1 }}>
                  ${stats.latest.price}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {stats.changes.price.value > 0 ? '+' : ''}${stats.changes.price.value}
                <span style={{ marginLeft: 4 }}>({stats.changes.price.percent}%)</span>
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Sales Change */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Sales Change</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {getTrendIcon(stats.changes.sales.value)}
                <Typography variant="h6" sx={{ ml: 1 }}>
                  {formatNumber(stats.latest.sales)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {stats.changes.sales.value > 0 ? '+' : ''}{formatNumber(stats.changes.sales.value)}
                <span style={{ marginLeft: 4 }}>({stats.changes.sales.percent}%)</span>
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Rating Change */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Rating Change</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {getTrendIcon(stats.changes.rating.value)}
                <Typography variant="h6" sx={{ ml: 1 }}>
                  {stats.latest.rating}★
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {stats.changes.rating.value > 0 ? '+' : ''}{stats.changes.rating.value}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderChart = () => {
    if (!historyData?.history) return null;

    // Reverse data (oldest to newest)
    const chartData = [...historyData.history].reverse().map(record => ({
      date: new Date(record.scrape_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' }),
      rank: record.hot_rank,
      price: record.price,
      sales: record.monthly_sales,
      totalSales: record.total_sales,
      rating: record.rating
    }));

    return (
      <Box>
        {/* Sales Trend */}
        <Typography variant="h6" sx={{ mb: 2 }}>Sales Trend</Typography>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="sales" stroke="#8884d8" name="Monthly Sales" strokeWidth={2} />
            <Line type="monotone" dataKey="totalSales" stroke="#82ca9d" name="Total Sales" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>

        {/* Rank Trend */}
        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Rank Trend</Typography>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis reversed />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="rank" stroke="#ff7300" name="Rank" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>

        {/* Price Trend */}
        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Price Trend</Typography>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="price" stroke="#0088fe" name="Price ($)" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  const renderTable = () => {
    if (!historyData?.history) return null;

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#FFF3E0' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Rank</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Monthly Sales</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Total Sales</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Rating</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Reviews</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {historyData.history.map((record, index) => (
              <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                <TableCell>{new Date(record.scrape_date).toLocaleDateString('en-US')}</TableCell>
                <TableCell>
                  <Chip
                    label={`#${record.hot_rank}`}
                    size="small"
                    color={record.hot_rank <= 10 ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell>{formatCurrency(record.price)}</TableCell>
                <TableCell>{formatNumber(record.monthly_sales)}</TableCell>
                <TableCell>{formatNumber(record.total_sales)}</TableCell>
                <TableCell>{record.rating}★</TableCell>
                <TableCell>{formatNumber(record.review_count)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Product History Trends
          </Typography>
          {product && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {product.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ASIN: {product.asin}
              </Typography>
            </>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && historyData && (
          <>
            {/* Stats Cards */}
            {renderStats()}

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                <Tab label="Chart Analysis" />
                <Tab label="Data Details" />
              </Tabs>
            </Box>

            {/* Content */}
            {tabValue === 0 && renderChart()}
            {tabValue === 1 && renderTable()}

            {/* Data Range */}
            {historyData.dateRange && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                Data Range: {new Date(historyData.dateRange.from).toLocaleDateString('en-US')} ~ {new Date(historyData.dateRange.to).toLocaleDateString('en-US')}
                ({historyData.recordCount} records)
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {product && (
          <Button
            variant="contained"
            href={product.productUrl || product.product_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            View Product Page
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProductHistoryModal;
